<template>
  <Teleport to="body">
    <WizardModal
      v-if="isVisible"
      :show-modal="isVisible"
      :modal-title="t('Novo Atendimento')"
      :steps="wizardSteps"
      elem-id="pdv-cliente-wizard"
      @close="handleClose"
      @cancel="handleClose"
      @finish="handleFinish"
    >
      <template #step-0>
        <div class="cliente-selection" data-testid="pdv-cliente-wizard">
          <h4>{{ t('Selecionar ou Criar Cliente') }}</h4>
          <p>{{ t('Como você deseja prosseguir?') }}</p>

          <div class="selection-cards">
            <div class="selection-card" data-testid="btn-selecionar-cliente" @click="handleBuscarCliente">
              <i class="pi pi-search"></i>
              <span class="card-title">{{ t('Selecionar Cliente Existente') }}</span>
              <span class="card-desc">{{ t('Buscar em nossa base de clientes cadastrados') }}</span>
            </div>

            <div class="selection-card" data-testid="btn-criar-cliente" @click="handleCriarCliente">
              <i class="pi pi-plus"></i>
              <span class="card-title">{{ t('Criar Novo Cliente') }}</span>
              <span class="card-desc">{{ t('Cadastrar um novo cliente no sistema') }}</span>
            </div>
          </div>

          <div class="selected-client" v-if="selectedClient" data-testid="pdv-cliente-selecionado">
            <div class="client-card">
              <i class="pi pi-user"></i>
              <div class="client-details">
                <span class="client-name" data-testid="pdv-cliente-nome">{{ selectedClient.nome || selectedClient.cliente_nome }}</span>
                <span class="client-phone" v-if="selectedClient.telefone || selectedClient.telefone_completo">{{ selectedClient.telefone_completo || selectedClient.telefone }}</span>
                <span class="client-cpf" v-if="selectedClient.documento">{{ documentLabel }}: {{ selectedClient.documento }}</span>
              </div>
              <Button icon="pi pi-times" severity="secondary" text size="small" data-testid="pdv-cliente-wizard-clear-btn" @click="selectedClient = null" />
            </div>
          </div>
        </div>
      </template>
    </WizardModal>

    <!-- Inline client select modal (DataTable with "Selecionar" button) -->
    <ClienteSelectModal
      ref="clienteSelectRef"
      @select="handleClienteSelected"
    />
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import WizardModal from '../../../ui/WizardModal.vue';
import ClienteSelectModal from '../../../components/ClienteSelectModal.vue';
import { eventBus } from '../../../eventBus';
import { useSOCheckout } from '../../../composables/useSOCheckout';
import { useCliente } from '../../../composables/useCliente';
import { DOCUMENT_PRESENTATION } from '../../../composables/useCustomerInternationalConfig';
import { log, error as logError } from '../../../utils/DebugLogger';

type Id = number | string;

interface ClientRaw {
  cliente_id?: Id;
  id?: Id;
  cliente_nome?: string;
  nome?: string;
  cliente_franquia_id?: Id;
  franquia_id?: Id;
  cliente_ddi?: string;
  ddi?: string;
  cliente_ddd?: string;
  ddd?: string;
  cliente_telefone?: string;
  telefone?: string;
  telefone_completo?: string;
  cliente_cpf?: string;
  documento?: string;
  [key: string]: unknown;
}

interface NormalizedClient {
  cliente_id: number | null;
  cliente_nome: string;
  cliente_franquia_id: number | null;
  cliente_ddi: string;
  cliente_ddd: string;
  cliente_telefone: string;
  cliente_cpf: string;
  [key: string]: unknown;
}

interface ClienteSelectInstance {
  open: () => void;
}

interface CreateOrderResponse {
  dados?: {
    order_id?: Id;
    so_order_id?: Id;
    id?: Id;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

const { t } = useI18n();
const { createOrder } = useSOCheckout();
const { fetchCliente } = useCliente();

const isVisible = ref<boolean>(false);
const selectedClient = ref<NormalizedClient | null>(null);
const clienteSelectRef = ref<ClienteSelectInstance | null>(null);

// Document label dinâmico baseado no país da franquia do cliente
const documentType = ref<string>('cpf');
const documentLabel = computed<string>(() =>
  (DOCUMENT_PRESENTATION as Record<string, { label: string }>)[documentType.value]?.label || 'CPF'
);

const wizardSteps = computed(() => [
  { title: t('Cliente'), validate: () => !!selectedClient.value }
]);

const nextLabel = (): string => t('Continuar para Frente de Caixa');
const disableNext = (): boolean => !selectedClient.value;

const normalizeClient = (client: ClientRaw | null | undefined): NormalizedClient | null => {
  if (!client) {
    return null;
  }

  return {
    ...client,
    cliente_id: Number(client.cliente_id || client.id || 0) || null,
    cliente_nome: client.cliente_nome || client.nome || '',
    cliente_franquia_id: Number(client.cliente_franquia_id || client.franquia_id || 0) || null,
    cliente_ddi: client.cliente_ddi || client.ddi || '',
    cliente_ddd: client.cliente_ddd || client.ddd || '',
    cliente_telefone: client.cliente_telefone || client.telefone || '',
    cliente_cpf: client.cliente_cpf || client.documento || '',
  };
};

const loadAndSelectClient = async (client: ClientRaw): Promise<void> => {
  const normalized = normalizeClient(client);
  if (!normalized?.cliente_id) {
    selectedClient.value = normalized;
    return;
  }

  try {
    const fullClient = await fetchCliente(normalized.cliente_id) as ClientRaw;
    selectedClient.value = normalizeClient(fullClient);
  } catch (err) {
    logError('PDVClienteWizardModal', 'loadAndSelectClient', 'Error loading full client, using fallback', err);
    selectedClient.value = normalized;
  }
};

const handleClose = (): void => {
  isVisible.value = false;
  selectedClient.value = null;
};

const handleFinish = async (): Promise<void> => {
  if (!selectedClient.value) return;

  const clienteId = Number(selectedClient.value.cliente_id || 0);
  const franchiseId = Number(selectedClient.value.cliente_franquia_id || (selectedClient.value as { franquia_id?: Id }).franquia_id || 0);

  if (!clienteId || !franchiseId) {
    eventBus.emit('globalMessage.error', t('Cliente sem franquia válida para iniciar atendimento'));
    return;
  }

  log('PDVClienteWizardModal', 'handleFinish', 'Creating order for client', clienteId);
  try {
    const result = await createOrder({
      so_order_client_id: clienteId,
      so_order_franchise_id: franchiseId,
      so_order_origin: 'balcao'
    }) as unknown as CreateOrderResponse;

    const orderId = result.dados?.order_id || result.dados?.so_order_id || result.dados?.id;
    eventBus.emit('globalMessage.success', t('Atendimento iniciado'));
    eventBus.emit('pdv.order-created', { orderId });
    handleClose();
  } catch (err) {
    // The error toast is emitted by useSOCheckout.createOrder itself.
    logError('PDVClienteWizardModal', 'handleFinish', 'Error', err);
  }
};

const handleBuscarCliente = (): void => {
  log('PDVClienteWizardModal', 'handleBuscarCliente', 'Opening client select DataTable modal');
  if (clienteSelectRef.value) {
    clienteSelectRef.value.open();
  }
};

const handleCriarCliente = (): void => {
  log('PDVClienteWizardModal', 'handleCriarCliente', 'Opening client create modal');
  eventBus.emit('modal.cliente.open', { action: 'create' });
};

const handleClienteSelected = (client: ClientRaw): void => {
  log('PDVClienteWizardModal', 'handleClienteSelected', 'Client selected from table', client);
  loadAndSelectClient(client);
};

const handleClienteSaved = (data?: ClientRaw): void => {
  if (!isVisible.value) return;
  log('PDVClienteWizardModal', 'handleClienteSaved', 'New client created, auto-selecting', data);
  if (data?.cliente_id) {
    loadAndSelectClient(data);
  }
};

const handleModalOpen = (): void => {
  log('PDVClienteWizardModal', 'handleModalOpen', 'Opening');
  isVisible.value = true;
  selectedClient.value = null;
};

onMounted(() => {
  eventBus.on('modal.pdv-cliente-wizard.open', handleModalOpen);
  eventBus.on('cliente.saved', handleClienteSaved);
});

onBeforeUnmount(() => {
  eventBus.off('modal.pdv-cliente-wizard.open', handleModalOpen);
  eventBus.off('cliente.saved', handleClienteSaved);
});
</script>

<style scoped>
.cliente-selection {
  padding: 16px 0;
}

.selection-cards {
  display: flex;
  gap: 16px;
  margin: 20px 0;
}

.selection-card {
  flex: 1;
  border: 2px solid var(--p-surface-200);
  border-radius: 8px;
  padding: 24px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s, background-color 0.2s;
}

.selection-card:hover {
  border-color: var(--p-primary-color);
  background: var(--p-primary-50);
}

.selection-card i {
  font-size: 2rem;
  color: var(--p-primary-color);
  margin-bottom: 12px;
  display: block;
}

.card-title {
  display: block;
  font-weight: 600;
  margin-bottom: 4px;
}

.card-desc {
  display: block;
  font-size: 0.85rem;
  color: var(--p-text-muted-color);
}

.selected-client {
  margin-top: 20px;
}

.client-card {
  display: flex;
  align-items: center;
  gap: 12px;
  background: var(--p-primary-50);
  border: 1px solid var(--p-primary-200);
  border-radius: 8px;
  padding: 16px;
}

.client-card > i {
  font-size: 1.5rem;
  color: var(--p-primary-color);
}

.client-details {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.client-name {
  font-weight: 600;
}

.client-phone,
.client-cpf {
  font-size: 0.9rem;
  color: var(--p-text-muted-color);
}
</style>
